import {PLANET_HD_V1_ENABLED} from '../../../../config/featureFlags'
import {
  REMOTE_SENSING_PRODUCTS,
  isPlanetHdProduct,
} from '../../Fields/ViewField/ViewLand/Images/remoteSensingModel'
import {isRemoteSensingDateVisualizable} from '../../Fields/ViewField/ViewLand/Images/remoteSensingDateModel'

export const TECHNICAL_REPORT_SOURCE_KEY = '_remote_sensing'

export const STANDARD_TECHNICAL_IMAGE_TYPES = ['visible', 'ndvi', 'evi']
export const HD_TECHNICAL_IMAGE_TYPES = [...STANDARD_TECHNICAL_IMAGE_TYPES, 'ndre']
export const ALL_TECHNICAL_IMAGE_TYPES = HD_TECHNICAL_IMAGE_TYPES

export const getTechnicalReportImageTypes = (product) => (
  isPlanetHdProduct(product)
    ? HD_TECHNICAL_IMAGE_TYPES
    : STANDARD_TECHNICAL_IMAGE_TYPES
)

export const getTechnicalReportSourceMeta = (imageSelections = {}) => {
  const source = imageSelections?.[TECHNICAL_REPORT_SOURCE_KEY]
  const product = source?.product

  if (!Object.values(REMOTE_SENSING_PRODUCTS).includes(product)) {
    return null
  }

  return {
    product,
    acquisitions: source?.acquisitions && typeof source.acquisitions === 'object'
      ? source.acquisitions
      : {},
  }
}

export const getTechnicalReportProduct = (report = {}) => (
  report.product || getTechnicalReportSourceMeta(report.imageSelections)?.product || null
)

export const getTechnicalReportSourceLabel = (report = {}) => {
  const product = getTechnicalReportProduct(report)

  if (!product) {
    return null
  }

  return isPlanetHdProduct(product) ? 'Planet HD' : 'Sentinel'
}

const serializeAcquisitionQuality = (item) => {
  const providerQuality = item?.raw?.quality || {}
  const safeFractions = [
    'cloud_fraction',
    'shadow_fraction',
    'light_haze_fraction',
    'heavy_haze_fraction',
    'snow_fraction',
    'coverage_fraction',
    'valid_fraction',
  ].reduce((quality, key) => (
    providerQuality[key] == null
      ? quality
      : {...quality, [key]: providerQuality[key]}
  ), {})

  return {
    cloud_percent: item?.cloudPercent ?? null,
    valid_percent: item?.validPercent ?? null,
    warning:
      item?.qualityWarning ||
      item?.reason ||
      item?.raw?.quality_warning ||
      item?.raw?.rejection_reason ||
      null,
    ...safeFractions,
  }
}

const serializeAcquisition = (item) => ({
  acquisition_id: item?.acquisitionId || item?.acquisition_id || null,
  item_id: item?.itemId || item?.item_id || null,
  access_id: item?.accessId || item?.access_id || null,
  acquired_at: item?.raw?.acquired_at || item?.acquired_at || item?.date || null,
  status: item?.status || null,
  resolution_m: item?.resolutionMeters ?? item?.raw?.resolution_m ?? 3,
  quality: serializeAcquisitionQuality(item),
  algorithm_version:
    item?.algorithmVersion || item?.raw?.algorithm_version || null,
})

export const buildTechnicalReportSourceMeta = ({
  imageDateState = {},
  imageSelections = {},
  product,
}) => {
  const acquisitions = {}

  if (isPlanetHdProduct(product)) {
    Object.entries(imageSelections).forEach(([landId, selections]) => {
      if (landId === TECHNICAL_REPORT_SOURCE_KEY || !selections || typeof selections !== 'object') {
        return
      }

      const selectedDates = new Set(
        ALL_TECHNICAL_IMAGE_TYPES.flatMap((type) => selections[type] || [])
      )
      const itemsByDate = imageDateState?.byLand?.[landId]?.itemsByDate || {}

      selectedDates.forEach((dateValue) => {
        const item = itemsByDate[dateValue]
        if (!item?.acquisitionId || !isRemoteSensingDateVisualizable(item)) {
          return
        }

        if (!acquisitions[landId]) {
          acquisitions[landId] = {}
        }
        acquisitions[landId][dateValue] = serializeAcquisition(item)
      })
    })
  }

  return {product, acquisitions}
}

export const withTechnicalReportSource = (
  imageSelections,
  sourceMeta,
  {enabled = PLANET_HD_V1_ENABLED} = {}
) => {
  if (!enabled) {
    return imageSelections
  }

  return {
    ...imageSelections,
    [TECHNICAL_REPORT_SOURCE_KEY]: sourceMeta,
  }
}

export const hasCompleteHdAcquisitionSelection = ({
  imageDateState = {},
  imageSelections = {},
  imageTypes = HD_TECHNICAL_IMAGE_TYPES,
  selectedLandIds = [],
}) => selectedLandIds.every((landId) => {
  const selections = imageSelections[landId] || {}
  const itemsByDate = imageDateState?.byLand?.[landId]?.itemsByDate || {}

  return imageTypes.every((type) => (selections[type] || []).every((dateValue) => {
    const item = itemsByDate[dateValue]
    return isRemoteSensingDateVisualizable(item) && Boolean(item.acquisitionId)
  }))
})

export const getStoredTechnicalReportAcquisition = (imageSelections, landId, dateValue) => {
  const stored = getTechnicalReportSourceMeta(imageSelections)?.acquisitions?.[landId]?.[dateValue]

  if (!stored?.acquisition_id) {
    return null
  }

  return {
    acquisitionId: stored.acquisition_id,
    accessId: stored.access_id || null,
    algorithmVersion: stored.algorithm_version || null,
    cloudPercent: stored.quality?.cloud_percent ?? null,
    date: String(stored.acquired_at || dateValue).slice(0, 10),
    itemId: stored.item_id || null,
    qualityWarning: stored.quality?.warning || null,
    resolutionMeters: stored.resolution_m ?? null,
    status: stored.status || 'ready',
    validPercent: stored.quality?.valid_percent ?? null,
  }
}

export const hasCompleteStoredHdAcquisitionSelection = ({
  imageSelections = {},
  imageTypes = HD_TECHNICAL_IMAGE_TYPES,
  selectedLandIds = [],
}) => selectedLandIds.every((landId) => imageTypes.every((type) => (
  imageSelections?.[landId]?.[type] || []
).every((dateValue) => Boolean(
  getStoredTechnicalReportAcquisition(imageSelections, landId, dateValue)?.acquisitionId
))))
